/* eslint-disable max-lines */
import type {
  ArtworkFile,
  DesignLabFormType,
  DesignProjectResponse,
  MadeEditResponse,
  PlacementPayload,
  UpdateDesignProjectPayload
} from "@design-lab/shared/types";
import type { printOnDemand } from "common/utils/models";
import { notifyError } from "common/utils/app-monitoring/notify";
import type { DesignProjectsFieldsFragment } from "graphql-api/operations";
import type { models } from "jiffy-api";
import { createDefaultArtworkData } from "./artwork";
import { buildCompositionsData, buildQuantities } from "./composition";
import {
  createPlacementFormData,
  createPlacementPayload,
  getActiveFrontPlacement
} from "./placement";

// Helper to build placements from artwork data
function buildPlacements(artwork: DesignLabFormType["artwork"]): Record<string, PlacementPayload> {
  const placements: Record<string, PlacementPayload> = {};

  // Handle front placement
  const { placement: frontPlacement, position: frontPosition } = getActiveFrontPlacement(artwork);
  // Only include placement if it has valid artworkId AND valid dimensions
  if (
    frontPlacement?.artworkId &&
    frontPlacement?.size?.width > 0 &&
    frontPlacement?.size?.height > 0
  ) {
    placements["front"] = createPlacementPayload(frontPlacement, artwork.info.front, frontPosition);
  } else if (frontPlacement?.artworkId) {
    // Log to app monitoring when artworkId exists but dimensions are invalid
    // This helps track code paths that create invalid state
    notifyError("Design Lab: Front placement with invalid dimensions prevented from saving", {
      params: {
        placement: "front",
        position: frontPosition,
        artworkId: frontPlacement.artworkId,
        size: frontPlacement.size,
        hasSize: !!frontPlacement.size,
        width: frontPlacement.size?.width,
        height: frontPlacement.size?.height
      }
    });
  }

  // Handle back placement
  // Only include placement if it has valid artworkId AND valid dimensions
  if (artwork.back?.artworkId && artwork.back?.size?.width > 0 && artwork.back?.size?.height > 0) {
    placements["back"] = createPlacementPayload(artwork.back, artwork.info.back, "back");
  } else if (artwork.back?.artworkId) {
    // Log to app monitoring when artworkId exists but dimensions are invalid
    // This helps track code paths that create invalid state
    notifyError("Design Lab: Back placement with invalid dimensions prevented from saving", {
      params: {
        placement: "back",
        artworkId: artwork.back.artworkId,
        size: artwork.back.size,
        hasSize: !!artwork.back.size,
        width: artwork.back.size?.width,
        height: artwork.back.size?.height
      }
    });
  }

  return placements;
}

// Helper to process placement data
type PlacementProcessorResult = {
  artworkData: DesignLabFormType["artwork"];
  artworkFiles: { front?: ArtworkFile; back?: ArtworkFile };
};

function processPlacementData(
  placements: DesignProjectResponse["placements"],
  initialArtworkData: DesignLabFormType["artwork"]
): PlacementProcessorResult {
  const artworkData = { ...initialArtworkData };
  const artworkFiles: { front?: ArtworkFile; back?: ArtworkFile } = {};

  // Process front placement
  const frontPlacement = placements["front"] || placements["frontLeftChest"];
  if (frontPlacement) {
    const placementFormData = createPlacementFormData(frontPlacement);
    if (placementFormData) {
      const { placementData, info, artworkFile } = placementFormData;
      const isFrontLeftChest = frontPlacement.position === "front_left_chest";

      artworkData.selectedFrontPlacement = isFrontLeftChest ? "frontLeftChest" : "front";
      if (isFrontLeftChest) {
        artworkData.frontLeftChest = placementData;
      } else {
        artworkData.front = placementData;
      }
      artworkData.info.front = info;

      if (artworkFile) {
        artworkFiles.front = artworkFile;
      }
    }
  }

  // Process back placement
  const backPlacement = placements["back"];
  if (backPlacement) {
    const placementFormData = createPlacementFormData(backPlacement);
    if (placementFormData) {
      const { placementData, info, artworkFile } = placementFormData;
      artworkData.back = placementData;
      artworkData.info.back = info;

      if (artworkFile) {
        artworkFiles.back = artworkFile;
      }
    }
  }

  const tabParam =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("tab") : null;

  if (tabParam === "back" && backPlacement) {
    artworkData.currentPlacement = "back";
  } else if (tabParam === "front" && frontPlacement) {
    artworkData.currentPlacement = artworkData.selectedFrontPlacement;
  } else if (frontPlacement) {
    artworkData.currentPlacement = artworkData.selectedFrontPlacement;
  } else if (backPlacement) {
    artworkData.currentPlacement = "back";
  }

  return { artworkData, artworkFiles };
}

// Transform form data to API payload
export function formToApiPayload(formData: DesignLabFormType): UpdateDesignProjectPayload {
  const { artwork, compositions } = formData;

  return {
    madeDesignProject: {
      activeCompositionId: compositions.currentCompositionId,
      quantities: buildQuantities(compositions.addedCompositions)
    },
    madeDesignProjectPlacements: buildPlacements(artwork)
  };
}

// Transform API response to form data
export function apiResponseToFormData(
  response: DesignProjectResponse | MadeEditResponse,
  compositionId?: string,
  productData?: printOnDemand.getProduct.Response
): {
  formData: Partial<DesignLabFormType>;
  artworkFiles: {
    front?: ArtworkFile;
    back?: ArtworkFile;
  };
} {
  const { placements, quantities } = response;
  const artworkData = createDefaultArtworkData(productData);

  // Process placements
  const { artworkData: processedArtworkData, artworkFiles } = processPlacementData(
    placements,
    artworkData
  );

  // Determine activeCompositionId
  // For DesignProjectResponse, use activeCompositionId
  // For MadeEditResponse, use compositionId from url
  // If url doesn't contain compositionId, use MadeEditResponse's first composition
  let activeCompositionId: number = 0;
  if ("activeCompositionId" in response) {
    activeCompositionId = response.activeCompositionId;
  } else if (compositionId) {
    activeCompositionId = Number(compositionId);
  } else if ("compositions" in response) {
    activeCompositionId = response.compositions[0] ?? 0;
  }

  if (!activeCompositionId) {
    throw new Error("No compositionId found");
  }

  return {
    formData: {
      artwork: processedArtworkData,
      compositions: buildCompositionsData(quantities, activeCompositionId)
    },
    artworkFiles
  };
}

export function mapMadeDesignProjectToGetDesignProjectResponse(
  graphqlResponse: DesignProjectsFieldsFragment | undefined
): models.designLab.getLatestDesignProject.Response | undefined {
  if (!graphqlResponse) {
    return undefined;
  }

  // Transform products array to nested object structure
  // Structure: catalogNumber -> compositionId -> variantId -> { quantity: number }
  type QuantitiesMap = Record<string, Record<string, Record<string, { quantity: number }>>>;
  const quantities: QuantitiesMap = {};

  // Process products from the new GraphQL structure
  graphqlResponse.products.forEach(product => {
    if (!quantities[product.catalogNumber]) {
      quantities[product.catalogNumber] = {};
    }
    // Non-null: just assigned above
    const catalogEntry = quantities[product.catalogNumber]!;

    if (!catalogEntry[product.compositionId]) {
      catalogEntry[product.compositionId] = {};
    }
    // Non-null: just assigned above
    const compositionEntry = catalogEntry[product.compositionId]!;

    // Process the quantities array for each product
    product.quantities.forEach(q => {
      compositionEntry[q.variantId] = { quantity: q.quantity };
    });
  });

  // Transform placements array to object with specific keys
  const placements = {} as models.designLab.getLatestDesignProject.Placements;

  [graphqlResponse.frontPlacement, graphqlResponse.backPlacement].forEach(placement => {
    if (!placement) return;

    const placementData = {
      id: parseInt(placement.id, 10),
      width: placement.width,
      height: placement.height,
      side: placement.side,
      position: placement.position,
      customNeckDistances: placement.customNeckDistances,
      enhanceImage: placement.enhanceImage || false,
      enhancementRequestNote: placement.enhancementRequestNote || "",
      removeBackground: placement.removeBackground || false,
      editImageManual: placement.editImageManual || false,
      dtfArtwork: {
        id: parseInt(placement.dtfArtwork.id, 10),
        originalImageUrl: placement.dtfArtwork.originalImageUrl || "",
        withBgUrl: placement.dtfArtwork.imageWithBackgroundUrl || "",
        noBgUrl: placement.dtfArtwork.imageNoBackgroundUrl || "",
        noBgNoTrimUrl: placement.dtfArtwork.imageSliderPreviewUrl || "",
        manualEditUrl: placement.dtfArtwork.manualEditUrl || null,
        manualEditNoTrimUrl: placement.dtfArtwork.manualEditNoTrimUrl || null,
        canRemoveBg: placement.dtfArtwork.canRemoveBg,
        removeBackground: placement.removeBackground || false,
        enhanceImage: placement.enhanceImage || false,
        enhancementTier: placement.enhancementTier || "full_enhancements",
        editImageManual: placement.dtfArtwork.editImageManual || false,
        enhancedOriginalDpi: 0, // Not provided in GraphQL
        originalMetadata: {
          width: 0,
          height: 0,
          widthInch: 0,
          heightInch: 0,
          dpi: 0
        }, // Not provided in GraphQL
        enhancedMetadata: {
          width: 0,
          height: 0,
          widthInch: 0,
          heightInch: 0
        } // Not provided in GraphQL
      },
      // Pinning session is placement-scoped (front/back can share the same artwork)
      pinSession: placement.pinSession
        ? {
            id: placement.pinSession.id,
            state: placement.pinSession.state,
            summary: placement.pinSession.summary ?? null
          }
        : null
    } as models.designLab.getLatestDesignProject.Placement;

    // Map to correct placement key based on position
    if (placement.position === "front_left_chest") {
      placements.frontLeftChest = placementData;
    } else if (placement.position === "front") {
      placements.front = placementData;
    } else if (placement.position === "back") {
      placements.back = placementData;
    }
  });

  // Transform GraphQL response to match REST API response format
  return {
    id: parseInt(graphqlResponse.id, 10),
    uuid: graphqlResponse.uuid || "",
    // Runtime shape is CatalogQuantities (3-level: catalog -> composition -> variant -> { quantity })
    // but getLatestDesignProject.Response declares Record<string, Record<string, number>>.
    // The downstream consumer (useFormRestoration) handles the actual shape correctly.
    quantities:
      quantities as unknown as models.designLab.getLatestDesignProject.Response["quantities"],
    placements,
    activeCompositionId: graphqlResponse.activeCompositionId
      ? parseInt(graphqlResponse.activeCompositionId, 10)
      : 0
  };
}
